import api from './api';
import type {
  IdentityProvider,
  CreateProviderRequest,
  UpdateProviderRequest,
} from '../types';

type IdentityProviderApiResponse = any;
type CreateProviderApiRequest = {
  providerTemplateID?: number | null;
  name: string;
  clientId: string;
  clientSecret?: string;
  callbackUrl?: string;
  authorizationUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  revocationUrl?: string;
  scopes?: string;
  requiresPkce?: boolean | null;
  tokenEndpointAuthMethod?: string;
  metadata?: string;
  isActive: boolean;
};

const normalizeScopes = (scopes?: string[] | string): string | undefined => {
  if (scopes == null) return undefined;
  if (Array.isArray(scopes)) return scopes.join(' ').trim() || undefined;
  const trimmed = scopes.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const mapApiIdentityProvider = (item: IdentityProviderApiResponse): IdentityProvider => {
  const id = String(item.identityProviderID || item.identityProviderId || item.IdentityProviderID);
  const templateIdRaw = item.providerTemplateID ?? item.providerTemplateId ?? item.ProviderTemplateID ?? null;
  const templateId = templateIdRaw != null ? String(templateIdRaw) : null;

  const isCustomProvider =
    item.isCustomProvider === true ||
    templateId == null;

  const scopesValue = item.scopes ?? item.Scopes;
  const templateIsActive = item.providerTemplate?.isActive;

  return {
    id,
    templateId,
    templateName: item.providerTemplate?.name,
    templateIsActive,
    name: item.name,
    clientId: item.clientId,
    providerType: item.providerTemplate?.providerType || (isCustomProvider ? 'Custom' : 'Unknown'),
    isCustomProvider,
    isActive: item.isActive,
    createdAt: item.createdDate || item.createdAt,
    callbackUrl: item.callbackUrl,
    authorizationUrl: item.authorizationUrl,
    tokenUrl: item.tokenUrl,
    userInfoUrl: item.userInfoUrl,
    revocationUrl: item.revocationUrl,
    scopes: scopesValue,
    requiresPkce: item.requiresPkce,
    tokenEndpointAuthMethod: item.tokenEndpointAuthMethod,
  };
};

const mapCreateProviderRequestToApi = (data: CreateProviderRequest): CreateProviderApiRequest => {
  const providerTemplateID =
    data.templateId != null && String(data.templateId).trim().length > 0
      ? Number(data.templateId)
      : null;

  // TokenServer API expects `Scopes` as a single string (JSON array string or space-separated).
  // IdentityProviderService can parse both JSON array and space-separated strings.
  const scopes = normalizeScopes((data as any).scopesOverride ?? data.scopes);

  return {
    providerTemplateID,
    name: data.name,
    clientId: data.clientId,
    clientSecret: data.clientSecret,
    callbackUrl: data.callbackUrl,
    authorizationUrl: data.authorizationUrl,
    tokenUrl: data.tokenUrl,
    userInfoUrl: data.userInfoUrl,
    revocationUrl: data.revocationUrl,
    scopes,
    requiresPkce: data.requiresPkce ?? null,
    tokenEndpointAuthMethod: data.tokenEndpointAuthMethod,
    isActive: true,
  };
};

const mapUpdateProviderRequestToApi = (data: UpdateProviderRequest): Partial<CreateProviderApiRequest> => {
  const scopes = normalizeScopes((data as any).scopesOverride ?? data.scopes);
  const providerTemplateID =
    data.templateId != null && String(data.templateId).trim().length > 0
      ? Number(data.templateId)
      : undefined;

  return {
    providerTemplateID,
    name: data.name,
    clientId: data.clientId,
    clientSecret: data.clientSecret,
    callbackUrl: data.callbackUrl,
    authorizationUrl: data.authorizationUrl,
    tokenUrl: data.tokenUrl,
    userInfoUrl: data.userInfoUrl,
    revocationUrl: data.revocationUrl,
    scopes,
    requiresPkce: data.requiresPkce ?? null,
    tokenEndpointAuthMethod: data.tokenEndpointAuthMethod,
    isActive: data.isActive ?? true,
  };
};

export class ProviderService {
  /**
   * Get all identity providers for the current user
   */
  async getUserProviders(): Promise<IdentityProvider[]> {
    const response = await api.get<any[]>('/TokenServer/IdentityProviders/List');

    // Map API response to IdentityProvider interface
    // API returns IdentityProviderID (long), ProviderTemplateID
    // Also includes nested ProviderTemplate
    return response.data.map(mapApiIdentityProvider);
  }

  /**
   * Get a specific identity provider by ID
   */
  async getProvider(providerId: string): Promise<IdentityProvider> {
    const response = await api.get<any>(`/TokenServer/IdentityProviders/${providerId}/Details`);

    return mapApiIdentityProvider(response.data);
  }

  /**
   * Create a new identity provider configuration
   */
  async createProvider(data: CreateProviderRequest): Promise<IdentityProvider> {
    // Backend DTO: IdentityProviderDto expects `providerTemplateID` and `scopes` (string).
    const payload = mapCreateProviderRequestToApi(data);
    const response = await api.post<any>('/TokenServer/IdentityProviders/Create', payload);
    return mapApiIdentityProvider(response.data);
  }

  /**
   * Update an existing identity provider
   */
  async updateProvider(
    providerId: string,
    data: UpdateProviderRequest
  ): Promise<IdentityProvider> {
    const payload = mapUpdateProviderRequestToApi(data);
    const response = await api.put<any>(`/TokenServer/IdentityProviders/${providerId}/Update`, payload);
    return mapApiIdentityProvider(response.data);
  }

  /**
   * Delete an identity provider and its associated tokens
   */
  async deleteProvider(providerId: string): Promise<void> {
    await api.delete(`/TokenServer/IdentityProviders/${providerId}/Delete`);
  }

  /**
   * Initiate OAuth authorization flow
   * Returns the authorization URL to redirect the user to
   */
  getAuthorizationUrl(providerId: string, callbackUrl?: string): string {
    const params = new URLSearchParams({
      providerId,
      ...(callbackUrl && { callbackUrl }),
    });

    // This URL should point to your Token Server Auth Custom module
    return `/auth/authorize?${params.toString()}`;
  }
}

// Export singleton instance
export const providerService = new ProviderService();
